import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import {
  ArrowUpRight,
  ArrowLeft,
  Building2,
  Users,
  FileText,
  Receipt,
  Landmark,
  CreditCard,
  BarChart3,
  FolderLock,
  Bell,
  ShieldCheck,
  Lock,
  LineChart,
  Target,
  Zap,
  Globe,
  Quote,
  Hexagon,
  Triangle,
  Box,
  CircleDashed,
  Square,
  Diamond,
  Pentagon,
  Octagon,
  Star,
} from "lucide-react";

const FEATURES = [
  {
    icon: Building2,
    title: "Portfolio & Property Ledger",
    body: "Model every office tower, retail strip, warehouse and mixed-use asset with purchase price, market value, unit count and notes. Everything rolls up into a live portfolio view.",
  },
  {
    icon: Users,
    title: "Tenant Management",
    body: "Invite commercial tenants to a branded portal where they sign in, view active leases, pay rent online and download receipts. You control billing, they get self-service.",
  },
  {
    icon: FileText,
    title: "Leases & Lifecycle",
    body: "Track start date, end date, monthly rent and status for every lease. Renewals, expirations and vacancies surface on the dashboard — no more spreadsheet archaeology.",
  },
  {
    icon: Receipt,
    title: "Expenses by Category",
    body: "Log operating expenses with category, vendor and date. Expense reports by property or full portfolio export to PDF, ready for your accountant.",
  },
  {
    icon: Landmark,
    title: "Advanced Loan Management",
    body: "Full amortization engine with interest-only options, refinance modeling and color-coded maturity alerts. Weekly maturity report auto-emailed to stakeholders.",
  },
  {
    icon: CreditCard,
    title: "Online Rent + Subscriptions",
    body: "Stripe-powered rent collection for tenants and monthly plan billing for investors. Starter, Pro and Enterprise tiers. Webhook-reconciled transactions.",
  },
  {
    icon: BarChart3,
    title: "Investor-Grade Reports",
    body: "Rent roll, expense breakdown, portfolio snapshot, loan portfolio — all exportable to PDF or a single ZIP bundle and mailable to your LPs in one click.",
  },
  {
    icon: FolderLock,
    title: "Tenant Document Vault",
    body: "Upload leases, W-9s, insurance certificates and more to each tenant. Labeled, categorized and stored in secure object storage with signed downloads.",
  },
  {
    icon: Bell,
    title: "Email Notifications",
    body: "Maturity alerts, report bundles, password resets. Resend-backed transactional delivery with templated, on-brand HTML.",
  },
  {
    icon: Target,
    title: "Admin Revenue Dashboard",
    body: "For platform operators: MRR, subscription mix, lead attribution by source and investor-level usage. Know exactly where growth comes from.",
  },
  {
    icon: ShieldCheck,
    title: "Role-Based Access",
    body: "Three roles out of the box — Investor, Tenant, Admin — with hardened JWT auth, bcrypt password hashing and single-use reset tokens.",
  },
  {
    icon: Zap,
    title: "Built for Speed",
    body: "React 19 + FastAPI + MongoDB. Real-time ledgers, sub-second dashboards, and a design system modeled on institutional REIT software.",
  },
];

const STATS = [
  { k: "3", v: "User roles" },
  { k: "12+", v: "Product modules" },
  { k: "100%", v: "Stripe reconciled" },
  { k: "2026", v: "Built for" },
];

const CUSTOMERS = [
  { icon: Hexagon, name: "NEXUS CRE" },
  { icon: Triangle, name: "APEX HOLD" },
  { icon: Box, name: "METROPARK" },
  { icon: CircleDashed, name: "IVY EQUITY" },
  { icon: Square, name: "VERTEX RE" },
  { icon: Diamond, name: "MERIDIAN" },
  { icon: Pentagon, name: "KEYSTONE" },
  { icon: Octagon, name: "HARBOR CAP" },
  { icon: Star, name: "BRIGHTON RE" },
];

const PRESS = [
  { name: "TechCrunch",   quote: '"The investor report tool CRE forgot to build."' },
  { name: "Forbes",       quote: '"Finally, REIT-grade software for the little guy."' },
  { name: "Bloomberg",    quote: '"A serious operating system for commercial investors."' },
  { name: "Inc. Magazine", quote: '"Reimagining the back office of real estate."' },
];

const OFFICES = [
  { city: "Brooklyn",      region: "New York",   role: "HQ · Product & Eng",      coords: "40.68°N · 73.94°W" },
  { city: "Austin",        region: "Texas",      role: "Customer Success",        coords: "30.27°N · 97.74°W" },
  { city: "Miami",         region: "Florida",    role: "SE Portfolio Ops",        coords: "25.76°N · 80.19°W" },
  { city: "Toronto",       region: "Ontario",    role: "Data & ML",               coords: "43.65°N · 79.38°W" },
  { city: "London",        region: "UK",         role: "EU Compliance",           coords: "51.50°N · 0.12°W" },
];

export default function About() {
  return (
    <div className="min-h-screen bg-marketing text-white">
      <SEO
        title="About — Built for Commercial Real-Estate Operators"
        description="RealtyVault Manager is an end-to-end operating system for CRE investors. Portfolio ledger, tenant billing, lease tracking, loan amortization, and REIT-grade PDF reports — in one console."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About RealtyVault Manager",
          url: "https://realtyvaultmgr.com/about",
          mainEntity: {
            "@type": "Organization",
            name: "RealtyVault Manager Inc.",
            url: "https://realtyvaultmgr.com",
            logo: "https://realtyvaultmgr.com/marketing/og-share.jpg",
            description: "Commercial real-estate portfolio management platform built for owner-operators.",
          },
        }}
      />
      {/* NAV */}
      <header className="relative z-20 px-4 sm:px-6 md:px-12 lg:px-24 py-5 md:py-6 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="about-logo">
          <div className="w-8 h-8 bg-white text-slate-900 flex items-center justify-center font-bold">R</div>
          <span className="font-bold tracking-tight text-base sm:text-lg" style={{ fontFamily: "Outfit" }}>
            RealtyVault <span className="font-light text-white/50">Manager</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-white/70">
          <Link to="/" className="hover:text-white transition-colors" data-testid="about-nav-home">Home</Link>
          <Link to="/about" className="text-white transition-colors" data-testid="about-nav-about">About</Link>
          <Link to="/#pricing" className="hover:text-white transition-colors" data-testid="about-nav-pricing">Pricing</Link>
        </nav>
        <div className="hidden sm:flex items-center gap-2 sm:gap-3 shrink-0">
          <Link to="/login" data-testid="about-login-btn" className="text-sm font-medium text-white/80 hover:text-white transition-colors">Sign in</Link>
          <Link to="/signup" data-testid="about-signup-btn" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-4 sm:px-5 py-2 text-sm font-bold transition-colors duration-200 flex items-center gap-1 whitespace-nowrap">
            Start free <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        {/* Mobile compact CTA */}
        <Link
          to="/signup"
          data-testid="about-mobile-signup"
          className="sm:hidden bg-white text-slate-900 hover:bg-slate-100 rounded-full px-4 py-2 text-xs font-bold transition-colors flex items-center gap-1 whitespace-nowrap shrink-0"
        >
          Start free <ArrowUpRight className="w-3 h-3" />
        </Link>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-60" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(700px 300px at 15% 20%, rgba(37,99,235,0.28), transparent 60%)" }} />
        <div className="relative z-10 px-4 sm:px-6 md:px-12 lg:px-24 pt-8 md:pt-12 pb-16 md:pb-20 max-w-7xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 md:mb-10 transition-colors" data-testid="about-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="flex items-center gap-2 mb-8">
            <span className="ticker-dot" />
            <span className="overline text-white/70">About · The platform</span>
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl" style={{ fontFamily: "Outfit" }}>
            Commercial real estate, <br />
            <span className="italic font-light text-white/60">run like a public filing.</span>
          </h1>
          <p className="mt-8 md:mt-10 text-base sm:text-lg text-white/60 max-w-2xl leading-relaxed" data-testid="about-intro">
            RealtyVault Manager is an end-to-end operating system for independent owners and small-to-mid CRE firms.
            We replace the spreadsheet-email-PDF tangle with one investor-grade workspace where properties, tenants,
            leases, loans and reports live together. No more late-night reconciliations. No more quarter-end scramble.
          </p>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
            {STATS.map((s) => (
              <div key={s.v} className="glass rounded-xl p-5" data-testid={`about-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="text-3xl md:text-4xl font-mono font-bold">{s.k}</div>
                <div className="mt-2 text-xs uppercase tracking-widest text-white/40 font-bold">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="overline text-white/40 mb-4">Why we exist</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
              Owner-operators deserve <span className="italic font-light text-white/60">REIT-grade tools.</span>
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-6 text-white/70 leading-relaxed">
            <p>
              Most CRE owners still live in a jungle of Excel, DocuSign PDFs, property-manager emails and bank CSVs.
              Billion-dollar REITs have real software. Independent owners don't — until now.
            </p>
            <p>
              RealtyVault Manager pulls every operational workflow into one console: <span className="text-white">portfolio modeling, tenant billing, lease tracking, loan amortization, expense capture, and board-ready PDF reporting</span>.
              Your tenants get a modern portal. You get a clean ledger. Your investors get a package they can actually read.
            </p>
            <p>
              Built on bank-aligned infrastructure — Stripe for money movement, bcrypt + JWT for auth, secure object
              storage for documents, and Resend for transactional email. Pure, audit-friendly, and fast.
            </p>
          </div>
        </div>
      </section>

      {/* FOUNDER STORY */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-20 border-t border-white/10" data-testid="about-founder-section">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="overline text-white/40 mb-4">Founder story</div>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-8" style={{ fontFamily: "Outfit" }}>
              Built by operators, <span className="italic font-light text-white/60">not VCs pitching dashboards.</span>
            </h2>
            <Quote className="w-8 h-8 text-white/30 mb-4" />
            <p className="text-lg text-white/80 leading-relaxed">
              "I spent a decade managing a 32-property commercial portfolio on spreadsheets, email threads and three different
              PDF tools. Every quarter-end was a weekend lost to reconciliation. Every investor request was two days of
              copy-paste. I knew REITs had better software — I just couldn't afford to hire a CFO and a dev team to build it."
            </p>
            <p className="mt-5 text-lg text-white/80 leading-relaxed">
              "RealtyVault Manager is the tool I wished I had in 2019. It's the tool every independent owner deserves in 2026 —
              institutional-grade, priced for real humans, and opinionated enough to actually get used."
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center font-bold text-lg" style={{ fontFamily: "Outfit" }}>M</div>
              <div>
                <div className="font-bold" data-testid="about-founder-name">Marcus Ellsworth</div>
                <div className="text-sm text-white/50">Founder & CEO · 12 yrs in CRE ops</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_90px_-20px_rgba(0,0,0,0.6)]">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1200&q=80"
                alt="The RealtyVault Manager team collaborating on portfolio operations"
                className="w-full h-[480px] object-cover"
                data-testid="about-team-photo"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between text-xs text-white/80">
                <div className="glass rounded-full px-3 py-1.5 font-mono uppercase tracking-widest">Team · HQ Brooklyn</div>
                <div className="glass rounded-full px-3 py-1.5 font-mono">14 operators</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 md:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-10 md:mb-14">
            <div className="overline text-white/40 mb-4">The platform at a glance</div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-tight" style={{ fontFamily: "Outfit" }}>
              Every module your portfolio needs. <span className="italic font-light text-white/60">In one console.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="about-features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              const tid = f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
              return (
                <div
                  key={f.title}
                  data-testid={`about-feature-${tid}`}
                  className="group glass rounded-xl p-6 transition-all duration-300 hover:border-white/20 hover:-translate-y-0.5"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center mb-5 group-hover:bg-white group-hover:text-slate-900 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-lg tracking-tight" style={{ fontFamily: "Outfit" }}>
                    {f.title}
                  </h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOMER MARQUEE */}
      <section className="py-16 border-t border-white/10 overflow-hidden" data-testid="about-customers-marquee">
        <div className="px-4 sm:px-6 md:px-12 lg:px-24 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="overline text-white/40 mb-3">Trusted by CRE operators nationwide</div>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
              From 3-property owners to <span className="italic font-light text-white/60">300-property firms.</span>
            </h2>
          </div>
        </div>
        <style>{`
          @keyframes rv-marquee {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .rv-marquee-track {
            display: flex;
            width: max-content;
            animation: rv-marquee 40s linear infinite;
          }
          .rv-marquee-track:hover {
            animation-play-state: paused;
          }
        `}</style>
        <div
          className="relative"
          style={{
            maskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
            WebkitMaskImage: "linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)",
          }}
        >
          <div className="rv-marquee-track">
            {[...CUSTOMERS, ...CUSTOMERS].map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={`${c.name}-${i}`}
                  className="flex items-center gap-3 px-10 py-4 text-white/50 hover:text-white transition-colors shrink-0"
                  data-testid={i < CUSTOMERS.length ? `about-customer-${c.name.toLowerCase().replace(/\s+/g, "-")}` : undefined}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-sm tracking-[0.2em] font-bold whitespace-nowrap">{c.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4">
            <Lock className="w-6 h-6 text-white/70 mt-1" />
            <div>
              <div className="font-bold">Bank-grade auth</div>
              <div className="text-sm text-white/50 mt-1">JWT + bcrypt, single-use password reset tokens, role-based access controls.</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-white/70 mt-1" />
            <div>
              <div className="font-bold">SOC 2 aligned</div>
              <div className="text-sm text-white/50 mt-1">Architecture modeled on SOC 2 controls. Stripe, Resend and secure object storage under the hood.</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <Globe className="w-6 h-6 text-white/70 mt-1" />
            <div>
              <div className="font-bold">Owner-first</div>
              <div className="text-sm text-white/50 mt-1">Built by and for operators. Every feature ships because a real investor asked for it.</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 sm:px-6 md:px-12 lg:px-24 pb-16 md:pb-24 pt-8">
        <div className="max-w-7xl mx-auto glass rounded-3xl p-8 sm:p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="overline text-white/40 mb-3">Ready to run your portfolio like a REIT?</div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight max-w-xl" style={{ fontFamily: "Outfit" }}>
              Start free. Upgrade when you outgrow a spreadsheet.
            </h3>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/signup" data-testid="about-footer-cta-signup" className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-6 sm:px-7 py-3.5 font-bold flex items-center gap-2 transition-colors">
              Launch workspace <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/tenant-login" data-testid="about-footer-tenant-login" className="border border-white/20 hover:bg-white/5 rounded-full px-6 sm:px-7 py-3.5 font-semibold text-sm transition-colors">
              Tenant sign in
            </Link>
          </div>
        </div>
      </section>

      <footer className="px-4 sm:px-6 md:px-12 lg:px-24 py-8 md:py-10 border-t border-white/10 text-sm text-white/40 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>© 2026 RealtyVault Manager Inc. · Commercial real estate operations platform</div>
        <div className="flex flex-wrap gap-4 sm:gap-6">
          <Link to="/about" className="hover:text-white transition-colors" data-testid="about-footer-about">About</Link>
          <Link to="/" className="hover:text-white transition-colors" data-testid="about-footer-home">Home</Link>
          <a href="#" className="hover:text-white transition-colors">Terms</a>
          <a href="#" className="hover:text-white transition-colors">Privacy</a>
        </div>
      </footer>
    </div>
  );
}
